import type { LedgerEntry, RegistryState } from '../data/types';
import { computeHoldings } from './holdings';

export interface ClassSummary {
  fundId: string;
  classId: string;
  units: number;
  value: number;
  invested: number;
  capitalReturned: number;
  incomeReceived: number;
  taxWithheld: number;
  periodEntries: LedgerEntry[];
}

const INVESTED = new Set(['ISSUE', 'CALL_PAYMENT', 'TRANSFER_IN', 'MIGRATION_OPENING']);
const RETURNED = new Set(['CAPITAL_RETURN', 'REDEMPTION', 'TRANSFER_OUT']);

export function investorSummary(
  state: RegistryState,
  holderId: string,
  asAt: string,
  periodStart: string,
  fundIds?: string[],
): ClassSummary[] {
  const inScope = (fundId: string) => !fundIds || fundIds.includes(fundId);
  const byClass = new Map<string, ClassSummary>();
  for (const entry of state.ledger) {
    if (entry.holderId !== holderId || entry.effectiveDate > asAt || !inScope(entry.fundId))
      continue;
    let summary = byClass.get(entry.classId);
    if (!summary) {
      summary = {
        fundId: entry.fundId,
        classId: entry.classId,
        units: 0,
        value: 0,
        invested: 0,
        capitalReturned: 0,
        incomeReceived: 0,
        taxWithheld: 0,
        periodEntries: [],
      };
      byClass.set(entry.classId, summary);
    }
    const sign = entry.type === 'ADJUSTMENT' ? -1 : 1;
    const type = entry.type === 'ADJUSTMENT' ? entry.reversedType : entry.type;
    if (type && INVESTED.has(type)) summary.invested += sign * entry.amount;
    if (type && RETURNED.has(type)) summary.capitalReturned += sign * entry.amount;
    if (type === 'DISTRIBUTION') summary.incomeReceived += sign * entry.amount;
    if (type === 'WITHHOLDING') summary.taxWithheld += sign * entry.amount;
    if (entry.effectiveDate >= periodStart) summary.periodEntries.push(entry);
  }
  const holdings = computeHoldings(state.ledger, state.classes, state.commitments, asAt).filter(
    (holding) => holding.holderId === holderId,
  );
  for (const summary of byClass.values()) {
    const holding = holdings.find((item) => item.classId === summary.classId);
    summary.units = holding?.units ?? 0;
    summary.value = holding?.value ?? 0;
  }
  return Array.from(byClass.values());
}

export function quarterStart(quarterEnd: string): string {
  const [year, month] = quarterEnd.split('-').map(Number);
  const startMonth = month - 2;
  return `${year}-${String(startMonth).padStart(2, '0')}-01`;
}
